import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  OnInit,
  signal,
} from '@angular/core';
import { MatCardModule } from '@angular/material/card';
import { CDNVhostService } from '../../../../core/services/cdn-vhost.service';
import { BehaviorSubject, filter, map, of, shareReplay, switchMap, take, tap } from 'rxjs';
import { AsyncPipe } from '@angular/common';
import { StatusLabelComponent } from '../../../../shared/components/status-label/status-label.component';
import { Status } from '../../../../shared/models/status-label.model';
import { CDNVhost, VhostStatus, XDNFeatures } from '../../../../shared/models/cdn-vhost.models';
import { ClipboardModule } from '@angular/cdk/clipboard';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { MatButtonModule } from '@angular/material/button';
import { ReactiveFormsModule } from '@angular/forms';
import { MatSlideToggleChange, MatSlideToggleModule } from '@angular/material/slide-toggle';
import { ConfirmationDialogComponent } from '../../../../shared/dialogs/confirmation-dialog/confirmation-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { SnackbarComponent } from '../../../../shared/components/snackbar/snackbar.component';
import { StatusModalService } from '../../../../core/services/status-modal.service';
import { WebsocketService } from '../../../../core/services/websocket.service';
import { CdnFeaturesDialogComponent } from '../../dialogs/cdn-features-dialog/cdn-features-dialog.component';
import { WebsiteData } from '../../../../shared/models/website-overview.model';
import { MatRipple } from '@angular/material/core';
import { MatMenuModule } from '@angular/material/menu';
import { toObservable } from '@angular/core/rxjs-interop';

@Component({
  selector: 'app-cdn-vhost',
  imports: [
    MatCardModule,
    AsyncPipe,
    StatusLabelComponent,
    ClipboardModule,
    FontAwesomeModule,
    MatButtonModule,
    ReactiveFormsModule,
    MatSlideToggleModule,
    MatRipple,
    MatMenuModule,
  ],
  templateUrl: './cdn-vhost.component.html',
  styleUrl: './cdn-vhost.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CdnVhostComponent implements OnInit {
  private cdnVhostService = inject(CDNVhostService);

  private dialog = inject(MatDialog);

  private snackbar = inject(MatSnackBar);

  private statusModalService = inject(StatusModalService);

  private websocketService = inject(WebsocketService);

  websiteData = input.required<WebsiteData>();

  website = computed(() => this.websiteData().website);

  website$ = toObservable(this.website);

  refresh$ = new BehaviorSubject<CDNVhost | null | void>(undefined);

  cdnVhost$ = this.refresh$.pipe(
    switchMap((data) =>
      data || data === null ? of(data) : this.cdnVhostService.getVhost(this.website().id),
    ),
    shareReplay(1),
  );

  xdnFeatures$ = this.website$.pipe(
    switchMap((w) => this.cdnVhostService.getXDNFeatures(w.id)),
    shareReplay(1),
  );

  cdnEnabled = signal(false);

  loadingCDN = signal(false);

  xdnEnabled = signal(false);

  loadingXDN = signal(false);

  cdnCachingEnabled = signal(true);

  loadingCDNCaching = signal(false);

  dnsRecordsText$ = this.cdnVhost$.pipe(
    filter(Boolean),
    map(
      (cdn) => `
          @ A ${cdn.serverIps[0]?.serverIp}
          @ A ${cdn.serverIps[1]?.serverIp}
          www A ${cdn.serverIps[0]?.serverIp}
          www A ${cdn.serverIps[1]?.serverIp}`,
    ),
  );

  ngOnInit(): void {
    this.cdnVhost$.pipe(take(1)).subscribe((data) => {
      if (data?.status === VhostStatus.ACTIVE) this.cdnEnabled.set(true);
      if (data?.xdnEnabled) this.xdnEnabled.set(true);
    });
    this.xdnFeatures$
      .pipe(take(1), filter(Boolean))
      .subscribe(({ cacheBypass }) => this.cdnCachingEnabled.set(!cacheBypass));
  }

  addCDN(): void {
    this.cdnVhostService
      .createVhost({
        domain: this.website().domain,
        websiteId: this.website().id,
        originServerHostname: this.websiteData().server?.id || '',
        ip: [],
        xdnEnabled: true,
      })
      .subscribe({
        next: (data) => {
          this.snackbar.openFromComponent(SnackbarComponent, {
            data: {
              text: `CDN created successfully!`,
            },
          });
          this.refresh$.next(data);
        },
        error: (error) => {
          this.statusModalService.open({
            state: 'error',
            message: error.message,
          });
        },
      });
  }

  deleteCDN(): void {
    this.dialog
      .open(ConfirmationDialogComponent, {
        data: {
          title: 'Delete CDN Vhost',
          paragraphs: [
            `Deleting CDN Vhost will remove the CDN configuration and DNS records.`,
            `If the website is not using WPX for DNS management, the A records must be updated in the active DNS zone to point to the Origin IP addresses.`,
            `Are you sure you want to delete CDN Vhost for ${this.website().domain}?`,
          ],
          primaryButtonText: 'Delete CDN Vhost',
          type: 'delete',
        },
      })
      .afterClosed()
      .pipe(
        take(1),
        filter(Boolean),
        switchMap(() => this.cdnVhostService.deleteVhost(this.website().id)),
      )
      .subscribe({
        next: () => {
          this.snackbar.openFromComponent(SnackbarComponent, {
            data: {
              text: `CDN deleted successfully!`,
            },
          });
          this.refresh$.next(null);
        },
        error: (error) => {
          this.statusModalService.open({
            state: 'error',
            message: error.message,
          });
        },
      });
  }

  onCDNToggleChange(event: MatSlideToggleChange): void {
    event.source.toggle(); // To prevent visual toggle
    if (this.cdnEnabled()) this.disableCDNVhost();
    else this.enableCDNVhost();
  }

  onXDNToggleChange(event: MatSlideToggleChange): void {
    event.source.toggle(); // To prevent visual toggle
    if (this.xdnEnabled()) this.disableXDNFeatures();
    else this.enableXDNFeatures();
  }

  onCDNCacheToggleChange(event: MatSlideToggleChange): void {
    event.source.toggle(); // To prevent visual toggle
    this.toggleCDNCache(this.cdnCachingEnabled());
  }

  enableCDNVhost(): void {
    this.cdnVhostService
      .enableCDN(this.website().id)
      .pipe(
        tap(() => this.loadingCDN.set(true)),
        switchMap(() =>
          this.websocketService.listen<{ message: string }>('dialog').pipe(
            filter(
              (message) =>
                message.message === `CDN Activated for website with id: ${this.website().id}!`,
            ),
            take(1),
          ),
        ),
      )
      .subscribe({
        next: () => {
          this.cdnEnabled.set(true);
          this.refresh$.next();
        },
        error: (error) => {
          this.statusModalService.open({
            state: 'error',
            message: error.error?.error,
          });
        },
        complete: () => {
          this.loadingCDN.set(false);
        },
      });
  }

  disableCDNVhost(): void {
    this.cdnVhostService
      .disableCDN(this.website().id)
      .pipe(
        tap(() => this.loadingCDN.set(true)),
        switchMap(() =>
          this.websocketService.listen<{ message: string }>('dialog').pipe(
            filter(
              (message) =>
                message.message === `CDN Deactivated for website with id: ${this.website().id}!`,
            ),
            take(1),
          ),
        ),
      )
      .subscribe({
        next: () => {
          this.cdnEnabled.set(false);
          this.refresh$.next();
        },
        error: (error) => {
          this.statusModalService.open({
            state: 'error',
            message: error.error?.error,
          });
        },
        complete: () => {
          this.loadingCDN.set(false);
        },
      });
  }

  enableXDNFeatures(): void {
    this.dialog
      .open(ConfirmationDialogComponent, {
        data: {
          title: 'Enable CDN VHost',
          paragraphs: [
            `This action will add the website ${this.website().domain} to the CDN’s configuration.`,
            `Are you sure you want to enable the CDN Vhost for ${this.website().domain}? `,
          ],
          primaryButtonText: 'Enable CDN VHost',
          icon: ['fas', 'play'],
        },
      })
      .afterClosed()
      .pipe(
        take(1),
        filter((x) => !!x),
        switchMap(() => this.cdnVhostService.enableXDN(this.website().id)),
      )
      .subscribe({
        next: () => this.xdnEnabled.set(true),
        error: (error) => {
          this.statusModalService.open({
            state: 'error',
            message: error.error?.error,
          });
        },
        complete: () => this.loadingXDN.set(false),
      });
  }

  disableXDNFeatures(): void {
    this.dialog
      .open(ConfirmationDialogComponent, {
        data: {
          title: 'Disable CDN VHost',
          paragraphs: [
            `This action will remove the website ${this.website().domain} from the CDN’s configuration.`,
            `Are you sure you want to disable the CDN Vhost for ${this.website().domain}?`,
          ],
          primaryButtonText: 'Disable CDN VHost',
          icon: ['fas', 'pause'],
        },
      })
      .afterClosed()
      .pipe(
        take(1),
        filter((x) => !!x),
        tap(() => this.loadingXDN.set(true)),
        switchMap(() => this.cdnVhostService.disableXDN(this.website().id)),
      )
      .subscribe({
        next: () => this.xdnEnabled.set(false),
        error: (error) => {
          this.statusModalService.open({
            state: 'error',
            message: error.error?.error,
          });
        },
        complete: () => this.loadingXDN.set(false),
      });
  }

  toggleCDNCache(cacheBypass: boolean): void {
    this.xdnFeatures$
      .pipe(
        take(1),
        filter(Boolean),
        switchMap((data: XDNFeatures) =>
          this.cdnVhostService.setXDNFeatures(this.website().id, { ...data, cacheBypass }),
        ),
      )
      .subscribe({
        next: () => {
          this.snackbar.openFromComponent(SnackbarComponent, {
            data: {
              text: `CDN cache ${cacheBypass ? 'disabled' : 'enabled'}!`,
            },
          });
          this.cdnCachingEnabled.set(!cacheBypass);
        },
        error: (error) => {
          this.statusModalService.open({
            state: 'error',
            message: error.error?.error,
          });
        },
      });
  }

  clearCache(): void {
    this.cdnVhostService.clearCache(this.website().id).subscribe({
      next: () =>
        this.snackbar.openFromComponent(SnackbarComponent, {
          data: {
            text: `Cache cleared!`,
          },
        }),
      error: (error) => {
        this.statusModalService.open({
          state: 'error',
          message: error.error?.error,
        });
      },
    });
  }

  openCDNFeaturesDialog(): void {
    this.xdnFeatures$
      .pipe(
        switchMap((features) =>
          this.dialog
            .open(CdnFeaturesDialogComponent, {
              data: { subTitle: this.website().domain, features },
            })
            .afterClosed(),
        ),
        take(1),
        filter((x) => !!x),
        switchMap((data: XDNFeatures) =>
          this.cdnVhostService.setXDNFeatures(this.website().id, data),
        ),
      )
      .subscribe({
        next: () =>
          this.snackbar.openFromComponent(SnackbarComponent, {
            data: {
              text: `CDN features updated!`,
            },
          }),
        error: (error) => {
          this.statusModalService.open({
            state: 'error',
            message: error.error?.error,
          });
        },
      });
  }

  toStatus(key: VhostStatus): Status {
    return key in Status ? Status[key as unknown as keyof typeof Status] : Status.ERROR;
  }
}
